import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config';
import { getUserGroups, SharingGroup } from '../services/sharingService';

interface RecipeShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipeId: number;
  recipeName: string;
}

const RecipeShareModal: React.FC<RecipeShareModalProps> = ({
  isOpen,
  onClose,
  recipeId,
  recipeName,
}) => {
  const { token } = useAuth();
  const [groups, setGroups] = useState<SharingGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && token) {
      loadGroups();
    }
    if (!isOpen) {
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const loadGroups = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const userGroups = await getUserGroups(token);
      setGroups(userGroups);
    } catch {
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (group: SharingGroup) => {
    if (!token) return;
    try {
      setSharing(true);
      setError(null);
      const resp = await fetch(API_ENDPOINTS.SHARE_RECIPE(recipeId), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: group.id }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || 'Failed to share recipe');
        return;
      }
      const count = data.shared_count ?? 0;
      if (count === 0) {
        setSuccessMessage(`All members of "${group.name}" already have this recipe.`);
      } else {
        setSuccessMessage(`Shared with ${count} member${count !== 1 ? 's' : ''} of "${group.name}".`);
      }
    } catch {
      setError('Failed to share recipe');
    } finally {
      setSharing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="recipe-share-overlay" onClick={onClose}>
      <div className="recipe-share-modal" onClick={e => e.stopPropagation()}>
        <div className="recipe-share-header">
          <div className="recipe-share-title">Share Recipe</div>
          <button className="recipe-share-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="recipe-share-recipe-name">"{recipeName}"</div>
        <div className="recipe-share-subtitle">
          Choose a group — each member gets their own copy instantly.
        </div>

        {error && <div className="recipe-share-error">{error}</div>}
        {successMessage && (
          <div className="recipe-share-success">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        {loading ? (
          <div className="recipe-share-loading">Loading groups…</div>
        ) : groups.length === 0 ? (
          <div className="recipe-share-empty">
            <div className="recipe-share-empty-icon">👥</div>
            <div>No groups yet.</div>
            <div className="recipe-share-empty-hint">Create a group in the People tab, then invite family or friends.</div>
          </div>
        ) : (
          <ul className="recipe-share-group-list">
            {groups.map(group => (
              <li key={group.id} className="recipe-share-group-item">
                <div className="recipe-share-group-info">
                  <span className="recipe-share-group-name">{group.name}</span>
                  {group.member_count != null && (
                    <span className="recipe-share-group-count">{group.member_count} member{group.member_count !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <button
                  className="recipe-share-group-btn"
                  onClick={() => handleShare(group)}
                  disabled={sharing}
                >
                  Share
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RecipeShareModal;
